import React, { isValidElement, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import Message from '../../../base/react/components/web/Message';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { NOTIFICATION_TYPE } from '../../constants';
import { NotificationsTransitionContext } from '../NotificationsTransition';
import NoticeCard, { IN_CALL_NOTICE_PALETTE } from './NoticeCard';
/**
 * The notice variant of a Jitsi appearance (notifications mockups,
 * 2026-11-02): error and warning use the error look, success the success
 * look, the rest info.
 */
export function noticeVariantFor(appearance) {
    if (appearance === NOTIFICATION_TYPE.ERROR || appearance === NOTIFICATION_TYPE.WARNING)
        return 'error';
    if (appearance === NOTIFICATION_TYPE.SUCCESS)
        return 'success';
    return 'info';
}
const useStyles = makeStyles()((theme) => {
    return {
        title: {
            ...withPixelLineHeight(theme.typography.bodyShortBold),
            display: 'block'
        },
        description: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            maxHeight: '150px',
            overflow: 'auto',
            overflowWrap: 'break-word',
            userSelect: 'all'
        },
        more: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(2)
        },
        moreAction: {
            background: 'transparent',
            border: 0,
            color: 'inherit',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 600,
            minHeight: 44,
            padding: 0,
            textDecoration: 'underline',
            '&.destructive': {
                color: IN_CALL_NOTICE_PALETTE.bad
            }
        }
    };
});
/**
 * A Jitsi notification in the call room, drawn as the in-call notice: dark
 * glass for every tenant, the action in the tenant colours, role=alert for
 * errors and role=status for the rest.
 */
const Notification = ({ appearance = NOTIFICATION_TYPE.NORMAL, customActionHandler, customActionNameKey, customActionType, description, descriptionArguments, descriptionKey, hideErrorSupportLink, onDismissed, title, titleArguments, titleKey, uid }) => {
    const { classes, cx, theme } = useStyles();
    const { t } = useTranslation();
    const { unmounting } = useContext(NotificationsTransitionContext);
    const onDismiss = useCallback(() => {
        onDismissed(uid);
    }, [uid]);
    const renderDescription = () => {
        const descriptionArray = [];
        descriptionKey
            && descriptionArray.push(t(descriptionKey, descriptionArguments));
        description && typeof description === 'string' && descriptionArray.push(description);
        // Keeping in mind that:
        // - Notifications that use the `translateToHtml` function get an element-based description array with one entry
        // - Message notifications receive string-based description arrays that might need additional parsing
        // We look for ready-to-render elements, and if present, we roll with them
        // Otherwise, we use the Message component that accepts a string `text` prop
        const shouldRenderHtml = descriptionArray.length === 1 && isValidElement(descriptionArray[0]);
        if (!descriptionArray.length && typeof description !== 'object')
            return null;
        // the id is used for testing the UI
        return (React.createElement("span", { className: classes.description, "data-testid": descriptionKey },
            shouldRenderHtml ? descriptionArray : React.createElement(Message, { text: descriptionArray.join(' ') }),
            typeof description === 'object' && description));
    };
    const _onOpenSupportLink = () => {
        window.open(interfaceConfig.SUPPORT_URL, '_blank', 'noopener');
    };
    // One action: the notice's own (custom) action, else "Contact support"
    // on an error that has a support link. Without one, a close button.
    const actions = customActionNameKey?.length && customActionHandler?.length
        ? customActionNameKey.map((customAction, customActionIndex) => {
            return {
                content: t(customAction),
                onClick: () => {
                    if (customActionHandler?.[customActionIndex]()) {
                        onDismiss();
                    }
                },
                type: customActionType?.[customActionIndex],
                testId: customAction
            };
        })
        : appearance === NOTIFICATION_TYPE.ERROR && !hideErrorSupportLink && interfaceConfig.SUPPORT_URL
            ? [{ content: t('dialog.contactSupport'), onClick: _onOpenSupportLink }]
            : [];
    const [primary, ...more] = actions;
    const palette = {
        ...IN_CALL_NOTICE_PALETTE,
        action: theme.palette.action01,
        onAction: theme.palette.text01,
        quietAction: theme.palette.action01
    };
    const titleText = title || t(titleKey ?? '', titleArguments);
    return (React.createElement(NoticeCard, { actionLabel: primary?.content, actionTestId: primary?.testId, closeLabel: t('dialog.close'), edge: 'top', id: uid, leaving: Boolean(unmounting.get(uid ?? '')), onAction: primary?.onClick, onClose: onDismiss, palette: palette, testId: titleKey || descriptionKey, variant: noticeVariantFor(appearance) },
        titleText ? React.createElement("span", { className: classes.title }, titleText) : null,
        renderDescription(),
        more.length ? (React.createElement("span", { className: classes.more }, more.map(({ content, onClick, type, testId }) => (React.createElement("button", { className: cx(classes.moreAction, type), "data-testid": testId, key: content, onClick: onClick, type: 'button' }, content))))) : null));
};
export default Notification;
